import type React from "react"
import { useState, useEffect } from "react"
import {
  Container,
  Typography,
  Box,
  List,
  ListItemText,
  Radio,
  FormControlLabel,
  ListItemButton,
  CircularProgress,
  Avatar,
  Button,
} from "@mui/material"
import { useNavigate, useOutletContext } from "react-router-dom"
import PropertyFooterLayout from "../../../components/properties/PropertyFooterLayout"
import { Person } from "@mui/icons-material"
import { getAllHunters } from "../../../services/hunterService"

const progress = 15
const step = 3

type Hunter = {
  id: number
  contacts?: {
    fullName: string
  }
  profileImage: string | null
  rating: number
  propertiesUploadedCount: number
  isLeader?: boolean
}

type PublishOutletContext = {
  handleNavigateToCommissionPage: () => void
  propertyHunter: number | null
  setPropertyHunter: (id: number | null) => void
}

const PropertyHunterAssignmentPage: React.FC = () => {
  const [availableHunters, setAvailableHunters] = useState<Hunter[]>([])
  const [isLoadingHunters, setIsLoadingHunters] = useState(false)
  const [localSelectedHunter, setLocalSelectedHunter] = useState<number | null>(null)
  const navigate = useNavigate()
  const { handleNavigateToCommissionPage, propertyHunter, setPropertyHunter } = useOutletContext<PublishOutletContext>()

  useEffect(() => {
    if (propertyHunter !== null) {
      setLocalSelectedHunter(Number(propertyHunter))
    }
  }, [propertyHunter])

  useEffect(() => {
    const fetchHunters = async () => {
      setIsLoadingHunters(true)
      try {
        const token = localStorage.getItem("authToken")
        if (token) {
          const huntersData = await getAllHunters(token)
          setAvailableHunters(huntersData || [])
        }
      } catch (error) {
        console.error("Failed to fetch hunters:", error)
      } finally {
        setIsLoadingHunters(false)
      }
    }

    fetchHunters()
  }, [])

  const handleBack = () => {
    navigate(-1)
  }

  const handleContinue = () => {
    handleNavigateToCommissionPage()
  }

  const handleSkipHunter = () => {
    setLocalSelectedHunter(null)
    setPropertyHunter(null)

    localStorage.setItem("skipHunter", "true")

    handleNavigateToCommissionPage()
  }

  const handleHunterSelection = (hunterId: number) => {
    const hunterIdNumber = Number(hunterId)

    setLocalSelectedHunter(hunterIdNumber)
    setPropertyHunter(hunterIdNumber)
    
    localStorage.removeItem("skipHunter")
  }

  return (
    <Container maxWidth="md" sx={{ display: "flex", pb: 12, flexDirection: "column", alignItems: "center" }}>
      <Box p={4} sx={{ width: "80%" }}>
        <Typography variant="h4" gutterBottom mb={2}>
          Asignar Hunter a tu Propiedad
        </Typography>
        <Typography variant="body1" mb={4}>
          Si un Hunter te ayudó a conseguir esta propiedad, selecciónalo de la lista. 
          Si ningún Hunter te asistió, puedes continuar sin asignar uno.
        </Typography>

        {isLoadingHunters ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 200 }}>
            <CircularProgress sx={{ color: "green" }} />
          </Box>
        ) : availableHunters.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No hay hunters disponibles en este momento.
            </Typography>
          </Box>
        ) : (
          <List sx={{ width: "100%" }}>
            {availableHunters.map((hunter) => {
              const hunterId = Number(hunter.id)
              const isSelected = localSelectedHunter === hunterId

              return (
                <ListItemButton
                  key={hunterId}
                  onClick={() => handleHunterSelection(hunterId)}
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    border: isSelected ? "2px solid black" : "1px solid grey",
                    backgroundColor: isSelected ? "rgba(0, 0, 0, 0.04)" : "transparent",
                    transition: "all 0.3s",
                    cursor: "pointer",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Radio
                        checked={isSelected}
                        value={hunterId}
                        color="success"
                        onChange={() => handleHunterSelection(hunterId)}
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                        {hunter.profileImage ? (
                          <Avatar
                            src={hunter.profileImage}
                            alt={`Hunter ${hunterId}`}
                            sx={{
                              width: 50,
                              height: 50,
                              mr: 2,
                            }}
                          />
                        ) : (
                          <Avatar sx={{ width: 50, height: 50, mr: 2, bgcolor: isSelected ? "darkgreen" : "green" }}>
                            <Person />
                          </Avatar>
                        )}
                        <ListItemText
                          primary={hunter.contacts?.fullName || `Hunter ${hunterId}`}
                          secondary={`Rating: ${hunter.rating} | Propiedades: ${hunter.propertiesUploadedCount}`}
                          primaryTypographyProps={{
                            variant: "h6",
                            fontWeight: isSelected ? "bold" : "medium",
                            color: isSelected ? "text.primary" : "inherit",
                          }}
                          secondaryTypographyProps={{ variant: "body2", mt: 1 }}
                        />
                      </Box>
                    }
                    sx={{ width: "100%" }}
                  />
                </ListItemButton>
              )
            })}
          </List>
        )}

        {/* Botón para continuar sin seleccionar un hunter */}
        <Box display="flex" justifyContent="center" mt={3}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleSkipHunter}
            sx={{
              borderColor: "green",
              color: "green",
              "&:hover": {
                borderColor: "darkgreen",
                backgroundColor: "rgba(0, 128, 0, 0.04)",
              },
            }}
          >
            Continuar sin asignar Hunter
          </Button>
        </Box>
      </Box>

      <PropertyFooterLayout
        progress={progress}
        step={step}
        onBack={handleBack}
        onContinue={handleContinue}
        isContinueDisabled={false}
      />
    </Container>
  )
}

export default PropertyHunterAssignmentPage
